import type { Process } from "types/types";
import { SingleLineModule } from "./ExpandedTileModules";
import styled from "styled-components/macro";
import { useSelector } from "store/useSelector";
import {
  useCalcPreferredMetric,
  useRollupDisplayForProcess,
  useSingleProcessFullRangeDataDump,
} from "utils/customHooks/genericHooks";
import { useMapDataDumpToRollup } from "components/Dashboard/TrendChart/TrendChartContent/trendChartContentUtils";
import { useShouldFetchDataDumpsPerProcess } from "components/DataGetter/DataGetter";
import { useMetrics } from "components/Dashboard/GroupMetrics/useMetrics";
import { useFlightTranslation } from "utils/customHooks/useFlightTranslation";
import { useFlightConfig } from "components/DataGetter/FlightConfig/useFlightConfig";
import { useSettings } from "contexts/SettingsManager/SettingsManager";

/** Expanded Tile module showing metric percent */
export default function TileMetricPercentModule({
  process,
}: {
  process: Process;
}) {
  const { preferredMetricData } = useMetrics();
  const { tileScaleMultiplier } = useSettings();

  const dataDumpForProcess = useSelector(
    state => state.legacy.dataDumpsFullRangeByProcess[process.id]
  );
  const rollupDisplay = useRollupDisplayForProcess(process.id);
  const shouldUseDataDumps = useShouldFetchDataDumpsPerProcess();
  const dataDump = useSingleProcessFullRangeDataDump(process.id);
  const mapDataDumpToRollup = useMapDataDumpToRollup();
  const rollupToUse =
    shouldUseDataDumps && dataDump
      ? mapDataDumpToRollup(dataDump)
      : rollupDisplay;

  const calcPreferredMetric = useCalcPreferredMetric();
  const metricValue = calcPreferredMetric(rollupToUse, dataDumpForProcess);
  const isLoading = !rollupToUse && !dataDumpForProcess;
  const { translate } = useFlightTranslation();
  const percentValue =
    metricValue || metricValue === 0
      ? metricValue.toFixed(0) + preferredMetricData.units
      : "--";
  const [
    {
      general: { expandedProcessTileCompactMode },
    },
  ] = useFlightConfig();
  const textDisplay =
    (preferredMetricData.displayNameShort &&
      translate(preferredMetricData.displayNameShort)) ??
    (preferredMetricData.displayName &&
      translate(preferredMetricData.displayName));
  return (
    <SingleLineModule
      loading={isLoading}
      showThisShiftIndicator={!expandedProcessTileCompactMode}
      data={
        <TileMetricPercentStyles
          className={`${
            percentValue.length === 4 || textDisplay.length > 13 /* "100%" */
              ? "long"
              : ""
          }`}
          $tileScaleMultiplier={tileScaleMultiplier ** 0.45}
        >
          <div className="percentText lightText">{textDisplay}:</div>
          <div className={`percentValue`}>{percentValue}</div>
        </TileMetricPercentStyles>
      }
    />
  );
}

const TileMetricPercentStyles = styled.div<{ $tileScaleMultiplier: number }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding: 0 ${props => 1 * props.$tileScaleMultiplier}em;
  box-sizing: border-box;
  .lightText {
    font-weight: normal;
  }
  .percentText {
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow-wrap: anywhere;
    margin-right: ${props => -8 * props.$tileScaleMultiplier}px;
  }
  .percentValue {
    min-width: ${props => 32 * props.$tileScaleMultiplier}px;
    text-align: right;
  }
  &.long {
    .percentText {
      transform: translateX(${props => -4 * props.$tileScaleMultiplier}px);
      font-size: ${props => 0.9 * props.$tileScaleMultiplier}em;
    }
    .percentValue {
      transform: translateX(${props => 4 * props.$tileScaleMultiplier}px);
    }
  }
`;
